/*
 * Padel USA: footer newsletter capture with source tracking.
 * Owner: sections/footer-newsletter_custom.liquid. Dossier: custom-code-padelusa/features/footer-newsletter.md
 *
 * On submit (email only, no account):
 *  1. Klaviyo client subscription to the "Newsletter" list with custom_source "Footer newsletter",
 *     so Klaviyo records where the consent came from (Subscribed to List → method detail).
 *  2. Klaviyo "Newsletter Signup" event: source, page_type, page_path, referrer, utm_*; this is the
 *     per-signup record for "which page do people sign up from".
 *  3. Profile properties newsletter_source / newsletter_signup_page, sent ON the subscription (a
 *     separate client profile call raced the subscription and Klaviyo dropped it; 2026-10-22).
 *  4. klaviyo.identify({email}) so this browser's later Viewed Product / Added to Cart events land
 *     on the profile (same pattern as klaviyoIdentify in assets/pusa-popup-engine.js).
 *  5. Shopify.analytics `pusa_newsletter_signup` + dataLayer, so the OS pixel and GTM see it.
 *  6. Marks the browser as a known subscriber so the wheel popup stops showing.
 * If the Klaviyo call fails (blocked, offline), the native Shopify customer form posts instead
 * (tags newsletter + newsletter-footer), so no signup is lost.
 *
 * PRESERVE: custom_source "Footer newsletter", metric "Newsletter Signup", the data-* hooks,
 * and the localStorage key pusa_popup_known_subscriber (read by assets/pusa-popup-engine.js).
 */
(function () {
  'use strict';

  var REVISION = '2024-10-15';
  var CUSTOM_SOURCE = 'Footer newsletter';
  var METRIC = 'Newsletter Signup';
  var KNOWN_SUB_KEY = 'pusa_popup_known_subscriber';

  function isEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }

  function context(form) {
    var props = {
      source: form.getAttribute('data-source') || 'footer',
      page_type: form.getAttribute('data-page-type') || '',
      page_path: window.location.pathname,
      page_title: document.title
    };
    try {
      var q = new URLSearchParams(window.location.search);
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
        var v = q.get(k);
        if (v) props[k] = v;
      });
    } catch (e) { /* no-op */ }
    if (document.referrer) props.referrer = document.referrer;
    return props;
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify(body)
    });
  }

  // Identify THIS browser to klaviyo.js (sets $exchange_id in the __kla_id cookie) so later
  // on-site events attach to the profile; the client API calls alone never identify the browser
  // (Klaviyo audit 2026-10-20, G1). Email only: no properties, so newsletter_source is untouched.
  // klaviyo.js loads async from the app embed: poll up to 20s, then leave the call on the legacy
  // _learnq queue, which klaviyo.js drains whenever it loads. Ad-blocked: harmless no-op.
  function klaviyoIdentify(email) {
    var tries = 0;
    (function attempt() {
      var k = window.klaviyo;
      if (k && typeof k.identify === 'function') {
        try { var p = k.identify({ email: email }); if (p && p.catch) p.catch(function () {}); } catch (e) {}
        return;
      }
      if (++tries <= 40) { window.setTimeout(attempt, 500); return; }
      window._learnq = window._learnq || [];
      window._learnq.push(['identify', { $email: email }]);
    })();
  }

  function track(companyId, email, props) {
    var q = '?company_id=' + encodeURIComponent(companyId);
    klaviyoIdentify(email);
    try {
      post('https://a.klaviyo.com/client/events/' + q, {
        data: {
          type: 'event',
          attributes: {
            metric: { data: { type: 'metric', attributes: { name: METRIC } } },
            properties: props,
            profile: { data: { type: 'profile', attributes: { email: email } } }
          }
        }
      }).catch(function () {});
    } catch (e) { /* non-blocking */ }
    var analytics = { source: props.source, page_type: props.page_type, page_path: props.page_path };
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: 'pusa_newsletter_signup' }, analytics)); } catch (e) {}
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish('pusa_newsletter_signup', analytics);
      }
    } catch (e) {}
    try { window.localStorage.setItem(KNOWN_SUB_KEY, 'newsletter_signup'); } catch (e) {}
  }

  function init(form) {
    if (form.__fnl) return;
    form.__fnl = true;
    var companyId = form.getAttribute('data-company-id');
    var listId = form.getAttribute('data-list-id');
    var input = form.querySelector('input[type="email"]');
    var button = form.querySelector('[type="submit"]');
    var msg = form.querySelector('[data-fnl-msg]');
    var tpl = form.querySelector('template[data-fnl-success]');
    if (!input || !companyId || !listId) return; // native form still works

    function setError(text) {
      if (msg) msg.textContent = text || '';
      if (text) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }

    function showSuccess() {
      var field = form.querySelector('[data-fnl-field]');
      var label = form.querySelector('.fnl__label');
      if (label) label.remove();
      if (msg) msg.remove();
      if (field && tpl) {
        var node = tpl.content.firstElementChild.cloneNode(true);
        field.replaceWith(node);
        node.focus({ preventScroll: true });
      }
    }

    form.addEventListener('submit', function (e) {
      if (form.__fnlNative) return; // fallback: let the browser post the Shopify form
      e.preventDefault();
      var email = (input.value || '').trim();
      if (!email) { setError('Enter your email address.'); input.focus(); return; }
      if (!isEmail(email)) { setError('Enter a valid email address.'); input.focus(); return; }
      setError('');
      if (button) button.setAttribute('aria-busy', 'true');

      var props = context(form);
      var controller = 'AbortController' in window ? new AbortController() : null;
      var timer = setTimeout(function () { if (controller) controller.abort(); }, 10000);

      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: REVISION },
        signal: controller ? controller.signal : undefined,
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: CUSTOM_SOURCE,
              // No `subscriptions` field: this revision rejects it (see back-in-stock dossier); the
              // client subscription itself records email marketing consent.
              // Source properties ride on the subscription so they land atomically with the profile.
              profile: { data: { type: 'profile', attributes: {
                email: email,
                properties: { newsletter_source: props.source, newsletter_signup_page: props.page_path }
              } } }
            },
            relationships: { list: { data: { type: 'list', id: listId } } }
          }
        })
      }).then(function (res) {
        clearTimeout(timer);
        if (res.status === 202 || res.ok) {
          track(companyId, email, props);
          showSuccess();
          return;
        }
        if (res.status === 400) {
          if (button) button.removeAttribute('aria-busy');
          setError('Enter a valid email address.');
          return;
        }
        throw new Error('klaviyo ' + res.status);
      }).catch(function () {
        clearTimeout(timer);
        // Klaviyo unreachable: fall back to the native Shopify newsletter form.
        form.__fnlNative = true;
        try { window.localStorage.setItem(KNOWN_SUB_KEY, 'newsletter_signup'); } catch (err) {}
        if (typeof form.requestSubmit === 'function') form.requestSubmit(); else form.submit();
      });
    });
  }

  function boot() {
    document.querySelectorAll('form[data-footer-newsletter]').forEach(init);
  }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
})();
